// Page d'inscription.
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AuthLayout from "@/components/AuthLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Mail, Lock } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [cguAcceptees, setCguAcceptees] = useState(false);
  const [erreur, setErreur] = useState("");
  const [chargement, setChargement] = useState(false);

  const soumettre = async (e) => {
    e.preventDefault();
    setErreur("");
    if (password.length < 6) {
      setErreur("Le mot de passe doit contenir au moins 6 caractères.");
      return;
    }
    if (password !== confirmation) {
      setErreur("Les mots de passe ne correspondent pas.");
      return;
    }
    if (!cguAcceptees) {
      setErreur("Vous devez accepter les Conditions Générales d'Utilisation.");
      return;
    }
    setChargement(true);
    const res = await register(email, password, cguAcceptees);
    setChargement(false);
    if (res.ok) navigate("/onboarding");
    else setErreur(res.message);
  };

  return (
    <AuthLayout titre="Créer un compte" sousTitre="Commencez à gérer votre entreprise en quelques minutes.">
      <form onSubmit={soumettre} className="space-y-5" data-testid="formulaire-inscription">
        {erreur && (
          <div data-testid="message-erreur" className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {erreur}
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-[#1D3D3D] font-semibold">Adresse email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4A8A8A]" />
            <Input id="email" type="email" data-testid="champ-email" value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="vous@entreprise.pf" required
              className="pl-10 bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A]" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password" className="text-[#1D3D3D] font-semibold">Mot de passe</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4A8A8A]" />
            <Input id="password" type="password" data-testid="champ-mot-de-passe" value={password}
              onChange={(e) => setPassword(e.target.value)} placeholder="6 caractères minimum" required
              className="pl-10 bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A]" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirmation" className="text-[#1D3D3D] font-semibold">Confirmer le mot de passe</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4A8A8A]" />
            <Input id="confirmation" type="password" data-testid="champ-confirmation" value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)} placeholder="••••••••" required
              className="pl-10 bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A]" />
          </div>
        </div>
        <div className="flex items-start gap-2.5">
          <input
            id="cgu"
            type="checkbox"
            data-testid="case-cgu"
            checked={cguAcceptees}
            onChange={(e) => setCguAcceptees(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-[#4A8A8A]/40 text-[#1D3D3D] focus:ring-[#4A8A8A] cursor-pointer"
          />
          <Label htmlFor="cgu" className="text-sm text-[#1D3D3D]/80 font-normal leading-snug cursor-pointer">
            J'ai lu et j'accepte les{" "}
            <Link to="/cgu" target="_blank" data-testid="lien-cgu" className="text-[#4A8A8A] hover:text-[#2D5A5A] font-semibold underline">
              Conditions Générales d'Utilisation
            </Link>.
          </Label>
        </div>
        <Button type="submit" data-testid="bouton-inscription" disabled={chargement || !cguAcceptees}
          className="w-full bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white font-semibold h-11">
          {chargement ? <Loader2 className="h-4 w-4 animate-spin" /> : "Créer mon compte"}
        </Button>
        <p className="text-center text-sm text-[#1D3D3D]/70">
          Déjà inscrit ?{" "}
          <Link to="/login" data-testid="lien-connexion" className="text-[#4A8A8A] hover:text-[#2D5A5A] font-semibold">
            Se connecter
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
